<template>
	<div class="content">
		<base-header class="pb-6">
			<div class="row align-items-center py-4">
				<div class="col-lg-6 col-7">
					<h6 class="h2 text-white d-inline-block mb-0">{{ $t("pos.menu.report_sales") }}</h6>
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</div>
				<div class="col-lg-6 col-5 text-right">
					<base-button size="md" type="neutral" :disabled="loading || !rows.length" @click="exportXlsx">{{ $t("pos.common.export") }}</base-button>
				</div>
			</div>
		</base-header>

		<div class="container-fluid mt--6">
			<pos-install-alert :show="notInstalled"></pos-install-alert>
			<!-- table edge-to-edge in the card (as /backoffice/orders-overview): card body px-0, everything else in px-4 -->
			<card class="no-border-card" body-classes="px-0">
				<div class="px-4">
					<pos-report-filter v-model="filter" @search="load"></pos-report-filter>
					<div class="mb-3">
						<label class="form-control-label mr-3">{{ $t("pos.report.group_by") }}</label>
						<el-radio-group v-model="groupBy" size="small" @change="load">
							<el-radio-button label="day">{{ $t("pos.report.by_day") }}</el-radio-button>
							<el-radio-button label="device">{{ $t("pos.report.by_device") }}</el-radio-button>
							<el-radio-button label="seller">{{ $t("pos.report.by_seller") }}</el-radio-button>
							<el-radio-button label="event">{{ $t("pos.report.by_event") }}</el-radio-button>
						</el-radio-group>
					</div>

					<h5 class="mb-2">{{ $t("pos.report.chart_net") }}</h5>
					<pos-bar-chart :rows="chartRows" :series="chartSeries" :empty-text="$t('pos.report.no_data')" class="mb-4"></pos-bar-chart>
				</div>

				<el-table :data="rows" v-loading="loading" header-row-class-name="thead-light" show-summary :summary-method="summary" class="bon-table pos-table">
					<el-table-column :label="groupLabel" min-width="170">
						<template slot-scope="{ row }">{{ rowLabel(row) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.bills')" width="95" align="right">
						<template slot-scope="{ row }">{{ int(row.bill_count) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.items')" width="95" align="right">
						<template slot-scope="{ row }">{{ int(row.item_qty) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.gross')" min-width="130" align="right">
						<template slot-scope="{ row }">{{ money(row.gross) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.discount')" min-width="115" align="right">
						<template slot-scope="{ row }">{{ money(row.discount) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.net')" min-width="130" align="right">
						<template slot-scope="{ row }"><strong>{{ money(row.net) }}</strong></template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.cash')" min-width="150" align="right">
						<template slot-scope="{ row }">{{ money(row.cash_total) }} ({{ int(row.cash_count) }})</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.company_transfer')" min-width="170" align="right">
						<template slot-scope="{ row }">{{ money(row.company_transfer_total) }} ({{ int(row.company_transfer_count) }})</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.qr_thqr')" min-width="150" align="right">
						<template slot-scope="{ row }">{{ money(row.qr_thqr_total) }} ({{ int(row.qr_thqr_count) }})</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.qr_wechat')" min-width="150" align="right">
						<template slot-scope="{ row }">{{ money(row.qr_wechat_total) }} ({{ int(row.qr_wechat_count) }})</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.qr_truemoney')" min-width="150" align="right">
						<template slot-scope="{ row }">{{ money(row.qr_truemoney_total) }} ({{ int(row.qr_truemoney_count) }})</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.qr_shopee')" min-width="150" align="right">
						<template slot-scope="{ row }">{{ money(row.qr_shopee_total) }} ({{ int(row.qr_shopee_count) }})</template>
					</el-table-column>
				</el-table>
				<p class="text-muted small mt-3 mb-0 px-4">
					{{ $t("pos.report.voided_note", { n: int(voided.bill_count), amount: money(voided.amount) }) }}
				</p>
			</card>

			<!-- §12: one day + one event → the top 10 buyers by member id (B46 with date) -->
			<card v-if="topScope" v-loading="topLoading" body-classes="px-0">
				<div class="px-4">
					<h4 class="mb-1">{{ $t("pos.report.top_spenders_day") }}</h4>
					<small class="text-muted d-block mb-3">{{ $t("pos.report.top_spenders_day_hint") }}</small>
				</div>
				<el-table :data="topRows" header-row-class-name="thead-light" class="bon-table pos-table" :empty-text="$t('pos.report.no_data')">
					<el-table-column :label="$t('pos.report.rank')" prop="rank" width="80" align="right"></el-table-column>
					<el-table-column :label="$t('pos.report.mid')" width="110">
						<template slot-scope="{ row }">#{{ row.mid_text }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.bill_count')" width="110" align="right">
						<template slot-scope="{ row }">{{ int(row.bill_count) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.total_spent')" min-width="140" align="right">
						<template slot-scope="{ row }"><strong>{{ money(row.total) }}</strong></template>
					</el-table-column>
				</el-table>
			</card>
		</div>
	</div>
</template>

<script>
import { Table, TableColumn, RadioGroup, RadioButton } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { PosService } from "@/resource";
import { posMoney, posInt, posEventLabel } from "@/utils/pos";
import posPageMixin, { posDefaultFilter } from "./posPageMixin";
import PosReportFilter from "./PosReportFilter.vue";
import PosInstallAlert from "./PosInstallAlert.vue";
import PosBarChart from "./PosBarChart.vue";

export default {
	name: "PosReportSales",
	mixins: [posPageMixin],
	components: {
		RouteBreadCrumb,
		PosReportFilter,
		PosInstallAlert,
		PosBarChart,
		[Table.name]: Table,
		[TableColumn.name]: TableColumn,
		[RadioGroup.name]: RadioGroup,
		[RadioButton.name]: RadioButton
	},
	data() {
		return {
			filter: posDefaultFilter(),
			groupBy: "day",
			rows: [],
			totals: {},
			voided: { bill_count: 0, amount: 0 },
			lastParams: null,
			topRows: [],
			topLoading: false
		};
	},
	computed: {
		// §12: a single business date and a single event (not "all" / default set)
		topScope() {
			const f = this.filter || {};
			const r = f.range || [];
			return !!r[0] && r[0] === r[1] && Number(f.eventId) > 0;
		},
		groupLabel() {
			return this.$t(`pos.report.by_${this.groupBy}`);
		},
		chartSeries() {
			return [
				{ key: "cash", label: this.$t("pos.bill.cash"), color: "#2dce89" },
				{ key: "company", label: this.$t("pos.report.company_transfer_amount"), color: "#5e72e4" },
				{ key: "thqr", label: this.$t("pos.report.qr_thqr_amount"), color: "#fb6340" },
				{ key: "wechat", label: this.$t("pos.report.qr_wechat_amount"), color: "#11cdef" },
				{ key: "truemoney", label: this.$t("pos.report.qr_truemoney_amount"), color: "#f5365c" },
				{ key: "shopee", label: this.$t("pos.report.qr_shopee_amount"), color: "#ffb236" }
			];
		},
		chartRows() {
			return this.rows.map((r) => ({ label: this.rowLabel(r), values: { cash: Number(r.cash_total) || 0, company: Number(r.company_transfer_total) || 0, thqr: Number(r.qr_thqr_total) || 0, wechat: Number(r.qr_wechat_total) || 0, truemoney: Number(r.qr_truemoney_total) || 0, shopee: Number(r.qr_shopee_total) || 0 } }));
		}
	},
	mounted() {
		this.load();
	},
	methods: {
		money: posMoney,
		int: posInt,
		// The API sends the device-default-set group (event 0) with an empty
		// label; it is translated here.
		rowLabel(r) {
			return posEventLabel(this.groupBy === "event" ? r.key : null, r.label, (k) => this.$t(k));
		},
		async load() {
			const p = this.posParams(this.filter);
			if (!p) return;
			p.group_by = this.groupBy;
			this.loading = true;
			try {
				const res = await PosService.PosReportSalesSummary(p);
				const d = (res.data && res.data.Datum) || {};
				this.rows = d.rows || [];
				this.totals = d.totals || {};
				this.voided = d.voided || { bill_count: 0, amount: 0 };
				this.lastParams = p;
				this.notInstalled = false;
				this.loadTop();
			} catch (err) {
				this.rows = [];
				this.posError(err);
			} finally {
				this.loading = false;
			}
		},
		async loadTop() {
			if (!this.topScope) {
				this.topRows = [];
				return;
			}
			const f = this.filter;
			this.topLoading = true;
			try {
				// include_test=1 always: same numbers as the device daily summary (§12,
				// which ranks every device of the event, like the public board)
				const res = await PosService.reportEventMembers({ event_id: f.eventId, date: f.range[0], include_test: 1, limit: 10 });
				const d = (res.data && res.data.Datum) || {};
				this.topRows = (d.rows || []).slice(0, 10);
			} catch (err) {
				this.topRows = [];
				this.posError(err);
			} finally {
				this.topLoading = false;
			}
		},
		summary() {
			const t = this.totals || {};
			return [
				this.$t("pos.report.total"),
				posInt(t.bill_count),
				posInt(t.item_qty),
				posMoney(t.gross),
				posMoney(t.discount),
				posMoney(t.net),
				`${posMoney(t.cash_total)} (${posInt(t.cash_count)})`,
				`${posMoney(t.company_transfer_total)} (${posInt(t.company_transfer_count)})`,
				`${posMoney(t.qr_thqr_total)} (${posInt(t.qr_thqr_count)})`,
				`${posMoney(t.qr_wechat_total)} (${posInt(t.qr_wechat_count)})`,
				`${posMoney(t.qr_truemoney_total)} (${posInt(t.qr_truemoney_count)})`,
				`${posMoney(t.qr_shopee_total)} (${posInt(t.qr_shopee_count)})`
			];
		},
		exportXlsx() {
			const p = this.lastParams || this.posParams(this.filter);
			if (!p) return;
			const t = (k) => this.$t(k);
			const rows = this.rows.concat([{ label: t("pos.report.total"), ...this.totals }]);
			this.posExport("sales", p, [{
				name: t("pos.menu.report_sales"),
				rows,
				columns: [
					{ label: this.groupLabel, value: (r) => (r.key === undefined ? r.label : this.rowLabel(r)) },
					{ label: t("pos.report.bills"), value: "bill_count" },
					{ label: t("pos.report.items"), value: "item_qty" },
					{ label: t("pos.report.gross"), value: (r) => Number(r.gross) },
					{ label: t("pos.report.discount"), value: (r) => Number(r.discount) },
					{ label: t("pos.report.net"), value: (r) => Number(r.net) },
					{ label: t("pos.report.cash_amount"), value: (r) => Number(r.cash_total) },
					{ label: t("pos.report.cash_count"), value: "cash_count" },
					{ label: t("pos.report.company_transfer_amount"), value: (r) => Number(r.company_transfer_total) },
					{ label: t("pos.report.company_transfer_count"), value: "company_transfer_count" },
					{ label: t("pos.report.qr_thqr_amount"), value: (r) => Number(r.qr_thqr_total) },
					{ label: t("pos.report.qr_thqr_count"), value: "qr_thqr_count" },
					{ label: t("pos.report.qr_wechat_amount"), value: (r) => Number(r.qr_wechat_total) },
					{ label: t("pos.report.qr_wechat_count"), value: "qr_wechat_count" },
					{ label: t("pos.report.qr_truemoney_amount"), value: (r) => Number(r.qr_truemoney_total) },
					{ label: t("pos.report.qr_truemoney_count"), value: "qr_truemoney_count" },
					{ label: t("pos.report.qr_shopee_amount"), value: (r) => Number(r.qr_shopee_total) },
					{ label: t("pos.report.qr_shopee_count"), value: "qr_shopee_count" }
				]
			}]);
		}
	}
};
</script>
